import SwygWaveMark from '@/components/SwygWaveMark';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  Droplets,
  Target,
  Trophy,
  TrendingUp,
  ArrowRight,
  CheckCircle,
  Star,
  Users,
  Zap,
  Shield,
  Smartphone,
} from 'lucide-react';

export default function Landing() {
  return (
    <div className="min-h-screen bg-hydro-100 font-kumbh">
      {/* Navigation */}
      <nav className="fixed top-0 w-full bg-white/90 backdrop-blur-md border-b border-lilac-200 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center space-x-3">
              <SwygWaveMark size="md" />
              <div>
                <span className="text-2xl font-bold bg-gradient-to-r from-purple-600 to-purple-500 bg-clip-text text-transparent">
                  SWYG
                </span>
                <p className="text-xs text-deep-500 -mt-1">Corporate Wellness</p>
              </div>
            </div>
            <div className="flex items-center space-x-4">
              <Button
                variant="ghost"
                onClick={() => (window.location.href = '/login')}
                className="text-deep-600 hover:text-purple-600"
              >
                Sign In
              </Button>
              <Button
                onClick={() => (window.location.href = '/onboarding')}
                data-testid="button-get-started"
                className="bg-purple-500 hover:bg-purple-600 text-white shadow-lg hover:shadow-xl transition-all duration-300 rounded-full"
              >
                Get Started
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <div className="pt-20 pb-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <Badge className="mb-6 bg-lilac-100 text-purple-700 border-lilac-300 px-4 py-2 text-sm font-medium animate-fade-in">
              🚀 New: AI-Powered Hydration Insights
            </Badge>
            <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold text-deep-800 mb-6 leading-tight animate-fade-in-up">
              Transform Your
              <span className="bg-gradient-to-r from-purple-600 via-purple-500 to-lilac-500 bg-clip-text text-transparent block">
                Workplace Wellness
              </span>
            </h1>
            <p
              className="text-lg sm:text-xl md:text-2xl text-deep-600 mb-8 max-w-4xl mx-auto leading-relaxed animate-fade-in-up"
              style={{ animationDelay: '0.2s' }}
            >
              The ultimate corporate wellness platform that makes hydration tracking
              <span className="font-semibold text-deep-800"> fun, engaging, and effective</span>.
              Build healthier teams through gamified challenges and smart insights.
            </p>
            <div
              className="flex flex-col sm:flex-row gap-4 justify-center items-center mb-12 animate-fade-in-up"
              style={{ animationDelay: '0.4s' }}
            >
              <Button
                size="lg"
                onClick={() => (window.location.href = '/onboarding')}
                data-testid="button-start-free-trial"
                className="bg-purple-500 hover:bg-purple-600 text-white text-lg px-8 py-4 rounded-full shadow-xl hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-1"
              >
                Start Free Trial
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
              <Button
                size="lg"
                variant="outline"
                onClick={() => (window.location.href = '/login')}
                data-testid="button-sign-in"
                className="text-lg px-8 py-4 rounded-full border-2 border-lilac-300 bg-lilac-100 text-deep-700 hover:bg-lilac-200 transition-all duration-300"
              >
                Sign In
              </Button>
            </div>

            {/* Stats */}
            <div
              className="grid grid-cols-2 md:grid-cols-4 gap-6 sm:gap-8 max-w-4xl mx-auto animate-fade-in-up"
              style={{ animationDelay: '0.6s' }}
            >
              <div className="text-center p-4 rounded-xl hover:bg-white/50 transition-all duration-300">
                <div className="text-2xl sm:text-3xl font-bold text-purple-600 mb-2">10K+</div>
                <div className="text-sm sm:text-base text-deep-600">Active Users</div>
              </div>
              <div className="text-center p-4 rounded-xl hover:bg-white/50 transition-all duration-300">
                <div className="text-2xl sm:text-3xl font-bold text-purple-500 mb-2">500+</div>
                <div className="text-sm sm:text-base text-deep-600">Companies</div>
              </div>
              <div className="text-center p-4 rounded-xl hover:bg-white/50 transition-all duration-300">
                <div className="text-2xl sm:text-3xl font-bold text-purple-600 mb-2">95%</div>
                <div className="text-sm sm:text-base text-deep-600">Satisfaction</div>
              </div>
              <div className="text-center p-4 rounded-xl hover:bg-white/50 transition-all duration-300">
                <div className="text-2xl sm:text-3xl font-bold text-purple-500 mb-2">2.5L</div>
                <div className="text-sm sm:text-base text-deep-600">Avg Daily Intake</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Features Section */}
      <div className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-deep-800 mb-6">
              Why Choose{' '}
              <span className="bg-gradient-to-r from-purple-600 to-purple-500 bg-clip-text text-transparent">
                SWYG
              </span>
              ?
            </h2>
            <p className="text-xl text-deep-600 max-w-3xl mx-auto">
              Everything you need to build a healthier, more engaged workforce
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
            <Card
              className="group hover:shadow-2xl transition-all duration-300 border-lilac-200 shadow-lg hover:-translate-y-2 animate-fade-in-up"
              style={{ animationDelay: '0.1s' }}
            >
              <CardHeader className="text-center pb-4">
                <div className="w-16 h-16 bg-gradient-to-br from-purple-500 to-purple-600 rounded-2xl flex items-center justify-center mx-auto mb-6 group-hover:scale-110 transition-transform duration-300">
                  <Droplets className="w-8 h-8 text-white" />
                </div>
                <CardTitle className="text-2xl font-bold text-deep-800">Smart Tracking</CardTitle>
              </CardHeader>
              <CardContent className="text-center">
                <CardDescription className="text-lg text-deep-600 leading-relaxed">
                  AI-powered hydration tracking with intelligent reminders, personalized goals, and
                  real-time insights.
                </CardDescription>
                <div className="mt-6 flex items-center justify-center space-x-2 text-purple-600">
                  <CheckCircle className="w-5 h-5" />
                  <span className="font-medium">Auto-logging</span>
                </div>
              </CardContent>
            </Card>

            <Card
              className="group hover:shadow-2xl transition-all duration-300 border-lilac-200 shadow-lg hover:-translate-y-2 animate-fade-in-up"
              style={{ animationDelay: '0.2s' }}
            >
              <CardHeader className="text-center pb-4">
                <div className="w-16 h-16 bg-gradient-to-br from-green-500 to-emerald-600 rounded-2xl flex items-center justify-center mx-auto mb-6 group-hover:scale-110 transition-transform duration-300">
                  <Target className="w-8 h-8 text-white" />
                </div>
                <CardTitle className="text-2xl font-bold text-deep-800">Personal Goals</CardTitle>
              </CardHeader>
              <CardContent className="text-center">
                <CardDescription className="text-lg text-deep-600 leading-relaxed">
                  Customized hydration targets based on your activity level, climate, and personal
                  preferences.
                </CardDescription>
                <div className="mt-6 flex items-center justify-center space-x-2 text-green-600">
                  <CheckCircle className="w-5 h-5" />
                  <span className="font-medium">Adaptive targets</span>
                </div>
              </CardContent>
            </Card>

            <Card
              className="group hover:shadow-2xl transition-all duration-300 border-lilac-200 shadow-lg hover:-translate-y-2 animate-fade-in-up"
              style={{ animationDelay: '0.3s' }}
            >
              <CardHeader className="text-center pb-4">
                <div className="w-16 h-16 bg-gradient-to-br from-purple-500 to-lilac-500 rounded-2xl flex items-center justify-center mx-auto mb-6 group-hover:scale-110 transition-transform duration-300">
                  <Trophy className="w-8 h-8 text-white" />
                </div>
                <CardTitle className="text-2xl font-bold text-deep-800">Team Competition</CardTitle>
              </CardHeader>
              <CardContent className="text-center">
                <CardDescription className="text-lg text-deep-600 leading-relaxed">
                  Gamified challenges, leaderboards, and achievement badges to motivate your entire
                  team.
                </CardDescription>
                <div className="mt-6 flex items-center justify-center space-x-2 text-purple-600">
                  <CheckCircle className="w-5 h-5" />
                  <span className="font-medium">Team challenges</span>
                </div>
              </CardContent>
            </Card>

            <Card
              className="group hover:shadow-2xl transition-all duration-300 border-lilac-200 shadow-lg hover:-translate-y-2 animate-fade-in-up"
              style={{ animationDelay: '0.4s' }}
            >
              <CardHeader className="text-center pb-4">
                <div className="w-16 h-16 bg-gradient-to-br from-orange-500 to-red-500 rounded-2xl flex items-center justify-center mx-auto mb-6 group-hover:scale-110 transition-transform duration-300">
                  <TrendingUp className="w-8 h-8 text-white" />
                </div>
                <CardTitle className="text-2xl font-bold text-deep-800">
                  Advanced Analytics
                </CardTitle>
              </CardHeader>
              <CardContent className="text-center">
                <CardDescription className="text-lg text-deep-600 leading-relaxed">
                  Comprehensive insights and reports to track individual and company-wide wellness
                  progress.
                </CardDescription>
                <div className="mt-6 flex items-center justify-center space-x-2 text-orange-600">
                  <CheckCircle className="w-5 h-5" />
                  <span className="font-medium">Real-time reports</span>
                </div>
              </CardContent>
            </Card>

            <Card
              className="group hover:shadow-2xl transition-all duration-300 border-lilac-200 shadow-lg hover:-translate-y-2 animate-fade-in-up"
              style={{ animationDelay: '0.5s' }}
            >
              <CardHeader className="text-center pb-4">
                <div className="w-16 h-16 bg-gradient-to-br from-lilac-500 to-purple-500 rounded-2xl flex items-center justify-center mx-auto mb-6 group-hover:scale-110 transition-transform duration-300">
                  <Smartphone className="w-8 h-8 text-white" />
                </div>
                <CardTitle className="text-2xl font-bold text-deep-800">Mobile First</CardTitle>
              </CardHeader>
              <CardContent className="text-center">
                <CardDescription className="text-lg text-deep-600 leading-relaxed">
                  Beautiful, responsive design that works perfectly on all devices and screen sizes.
                </CardDescription>
                <div className="mt-6 flex items-center justify-center space-x-2 text-purple-600">
                  <CheckCircle className="w-5 h-5" />
                  <span className="font-medium">PWA ready</span>
                </div>
              </CardContent>
            </Card>

            <Card
              className="group hover:shadow-2xl transition-all duration-300 border-lilac-200 shadow-lg hover:-translate-y-2 animate-fade-in-up"
              style={{ animationDelay: '0.6s' }}
            >
              <CardHeader className="text-center pb-4">
                <div className="w-16 h-16 bg-gradient-to-br from-purple-500 to-purple-600 rounded-2xl flex items-center justify-center mx-auto mb-6 group-hover:scale-110 transition-transform duration-300">
                  <Shield className="w-8 h-8 text-white" />
                </div>
                <CardTitle className="text-2xl font-bold text-deep-800">
                  Enterprise Security
                </CardTitle>
              </CardHeader>
              <CardContent className="text-center">
                <CardDescription className="text-lg text-deep-600 leading-relaxed">
                  Bank-level security with SSO integration, data encryption, and compliance
                  features.
                </CardDescription>
                <div className="mt-6 flex items-center justify-center space-x-2 text-purple-600">
                  <CheckCircle className="w-5 h-5" />
                  <span className="font-medium">SOC 2 compliant</span>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>

      {/* Testimonials Section */}
      <div className="py-20 bg-gradient-to-br from-lilac-50 to-hydro-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-deep-800 mb-6">
              Loved by{' '}
              <span className="bg-gradient-to-r from-purple-600 to-purple-500 bg-clip-text text-transparent">
                Teams Worldwide
              </span>
            </h2>
            <p className="text-xl text-deep-600 max-w-3xl mx-auto">
              See what our customers are saying about their transformation
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <Card className="border-lilac-200 shadow-lg">
              <CardContent className="p-8">
                <div className="flex items-center mb-4">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} className="w-5 h-5 text-yellow-400 fill-current" />
                  ))}
                </div>
                <p className="text-deep-600 mb-6 italic">
                  "SWYG transformed our workplace culture. Our team's energy levels and productivity
                  have never been higher!"
                </p>
                <div className="flex items-center">
                  <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-purple-600 rounded-full flex items-center justify-center text-white font-bold">
                    SJ
                  </div>
                  <div className="ml-4">
                    <div className="font-semibold text-deep-800">Sarah Johnson</div>
                    <div className="text-deep-500">HR Director, TechCorp</div>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="border-lilac-200 shadow-lg">
              <CardContent className="p-8">
                <div className="flex items-center mb-4">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} className="w-5 h-5 text-yellow-400 fill-current" />
                  ))}
                </div>
                <p className="text-deep-600 mb-6 italic">
                  "The gamification features make staying hydrated fun. Our employees actually look
                  forward to the challenges!"
                </p>
                <div className="flex items-center">
                  <div className="w-12 h-12 bg-gradient-to-br from-green-500 to-emerald-600 rounded-full flex items-center justify-center text-white font-bold">
                    MR
                  </div>
                  <div className="ml-4">
                    <div className="font-semibold text-deep-800">Mike Rodriguez</div>
                    <div className="text-deep-500">CEO, WellnessFirst</div>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="border-lilac-200 shadow-lg">
              <CardContent className="p-8">
                <div className="flex items-center mb-4">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} className="w-5 h-5 text-yellow-400 fill-current" />
                  ))}
                </div>
                <p className="text-deep-600 mb-6 italic">
                  "The analytics dashboard gives us incredible insights into our team's wellness
                  patterns. Highly recommended!"
                </p>
                <div className="flex items-center">
                  <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-lilac-500 rounded-full flex items-center justify-center text-white font-bold">
                    AL
                  </div>
                  <div className="ml-4">
                    <div className="font-semibold text-deep-800">Anna Lee</div>
                    <div className="text-deep-500">Wellness Manager, HealthCo</div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>

      {/* CTA Section */}
      <div className="py-20 bg-gradient-to-r from-deep-900 via-purple-900 to-deep-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-4xl md:text-6xl font-bold text-white mb-6">
              Ready to Transform Your
              <span className="bg-gradient-to-r from-lilac-400 to-purple-400 bg-clip-text text-transparent block">
                Workplace Wellness?
              </span>
            </h2>
            <p className="text-xl md:text-2xl text-lilac-200 mb-12 leading-relaxed">
              Join thousands of companies improving employee wellness through smart hydration
              tracking. Start your free trial today - no credit card required.
            </p>
            <div className="flex flex-col sm:flex-row gap-6 justify-center items-center">
              <Button
                size="lg"
                onClick={() => (window.location.href = '/onboarding')}
                data-testid="button-start-tracking"
                className="bg-purple-500 hover:bg-purple-600 text-white text-xl px-12 py-6 rounded-full shadow-2xl transition-all duration-300 transform hover:-translate-y-1"
              >
                Start Free Trial
                <ArrowRight className="w-6 h-6 ml-3" />
              </Button>
              <div className="text-lilac-300">
                <div className="flex items-center space-x-2">
                  <CheckCircle className="w-5 h-5 text-green-400" />
                  <span>14-day free trial</span>
                </div>
                <div className="flex items-center space-x-2 mt-1">
                  <CheckCircle className="w-5 h-5 text-green-400" />
                  <span>No setup fees</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="bg-deep-900 text-white py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            <div>
              <div className="flex items-center space-x-3 mb-4">
                <SwygWaveMark size="md" />
                <span className="text-2xl font-bold">SWYG</span>
              </div>
              <p className="text-lilac-300">
                The ultimate corporate wellness platform for hydration tracking and team engagement.
              </p>
            </div>
            <div>
              <h3 className="font-semibold mb-4">Product</h3>
              <ul className="space-y-2 text-lilac-300">
                <li>
                  <a href="#" className="hover:text-white transition-colors">
                    Features
                  </a>
                </li>
                <li>
                  <a href="#" className="hover:text-white transition-colors">
                    Pricing
                  </a>
                </li>
                <li>
                  <a href="#" className="hover:text-white transition-colors">
                    API
                  </a>
                </li>
                <li>
                  <a href="#" className="hover:text-white transition-colors">
                    Integrations
                  </a>
                </li>
              </ul>
            </div>
            <div>
              <h3 className="font-semibold mb-4">Company</h3>
              <ul className="space-y-2 text-lilac-300">
                <li>
                  <a href="#" className="hover:text-white transition-colors">
                    About
                  </a>
                </li>
                <li>
                  <a href="#" className="hover:text-white transition-colors">
                    Blog
                  </a>
                </li>
                <li>
                  <a href="#" className="hover:text-white transition-colors">
                    Careers
                  </a>
                </li>
                <li>
                  <a href="#" className="hover:text-white transition-colors">
                    Contact
                  </a>
                </li>
              </ul>
            </div>
            <div>
              <h3 className="font-semibold mb-4">Support</h3>
              <ul className="space-y-2 text-lilac-300">
                <li>
                  <a href="#" className="hover:text-white transition-colors">
                    Help Center
                  </a>
                </li>
                <li>
                  <a href="#" className="hover:text-white transition-colors">
                    Documentation
                  </a>
                </li>
                <li>
                  <a href="#" className="hover:text-white transition-colors">
                    Status
                  </a>
                </li>
                <li>
                  <a href="/legal/privacy.html" className="hover:text-white transition-colors">
                    Privacy
                  </a>
                </li>
              </ul>
            </div>
          </div>
          <div className="border-t border-deep-700 mt-8 pt-8 text-center text-lilac-300">
            <p>&copy; 2024 SWYG. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
}
